/**
 * Request batching: GETs fired together go to the API as ONE call.
 *
 * A dashboard mounts ~15 widgets that each fetch their own data, plus the
 * shell (menu, preferences, counters…). With one backend process those ~30
 * calls queued behind each other, each paying the full per-request cost
 * (tenant lookup, token check, user load). Now every API GET issued within a
 * few milliseconds of another is collected and sent as `POST /v1/batch`; the
 * backend runs them in-process (all middleware, permissions and caches apply,
 * the token is checked once) and returns every response in one body.
 *
 * Transparent to callers: each request still resolves/rejects with its own
 * status and data, so interceptors (401 refresh, multilang stripping) work
 * unchanged. Identical GETs in one batch are sent once (each caller gets its own copy). A request that is alone goes out normally. Not batched: non-GETs,
 * blob/arraybuffer downloads, other hosts, `{ noBatch: true }`. If the batch
 * call itself fails, every request in it is sent on its own instead.
 */
import axios, {
  AxiosAdapter,
  AxiosError,
  AxiosHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { API_PREFIX } from './endpoints/api.constants';

const WINDOW_MS = 8;
const MAX_PER_BATCH = 40;

interface Pending {
  config: InternalAxiosRequestConfig;
  path: string;
  resolve: (r: AxiosResponse) => void;
  reject: (e: unknown) => void;
}

interface BatchItem {
  id: string;
  status: number;
  headers: Record<string, string>;
  body: unknown;
}

let batchingDisabled = false; // e.g. an older backend without /batch

function header(config: InternalAxiosRequestConfig, name: string): string {
  const h = config.headers as AxiosHeaders | Record<string, unknown> | undefined;
  const v = h && (typeof (h as AxiosHeaders).get === 'function'
    ? (h as AxiosHeaders).get(name)
    : (h as Record<string, unknown>)[name]);
  return v == null ? '' : String(v);
}

/** Path relative to API_PREFIX (with query), or null if not batchable. */
function batchablePath(config: InternalAxiosRequestConfig): string | null {
  if (batchingDisabled || !API_PREFIX) return null;
  if ((config.method || 'get').toLowerCase() !== 'get') return null;
  if (config.responseType && config.responseType !== 'json') return null;
  if ((config as { noBatch?: boolean }).noBatch) return null;
  if (config.onDownloadProgress) return null;
  const full = axios.getUri(config);
  if (!full.startsWith(`${API_PREFIX}/`)) return null;
  const rel = full.slice(API_PREFIX.length);
  return rel.startsWith('/batch') ? null : rel;
}

function settle(p: Pending, item: BatchItem): void {
  const response: AxiosResponse = {
    data: item.body,
    status: item.status,
    statusText: '',
    headers: new AxiosHeaders(item.headers),
    config: p.config,
    request: null,
  };
  const ok = !p.config.validateStatus || p.config.validateStatus(item.status);
  if (ok) {
    p.resolve(response);
  } else {
    p.reject(new AxiosError(
      `Request failed with status code ${item.status}`,
      item.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
      p.config, null, response,
    ));
  }
}

export function createBatchingAdapter(send: AxiosAdapter): AxiosAdapter {
  const groups = new Map<string, Pending[]>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const sendAlone = (p: Pending) => send(p.config).then(p.resolve, p.reject);

  async function sendBatch(list: Pending[]): Promise<void> {
    const live = list.filter((p) => {
      if (p.config.signal?.aborted) {
        p.reject(new axios.CanceledError(undefined, undefined, p.config));
        return false;
      }
      return true;
    });
    if (live.length === 0) return;
    if (live.length === 1) { sendAlone(live[0]); return; }

    // Widgets that ask for the same thing at the same moment (two "My learning" lists, two progress
    // summaries…) share one sub-request.
    const unique = [...new Set(live.map((p) => p.path))];
    const idOf = new Map(unique.map((path, i) => [path, String(i)]));

    const first = live[0].config;
    const headers = new AxiosHeaders();
    // i18n-ignore: HTTP header names
    for (const name of ['Authorization', 'X-Tenant-ID', 'Tenant-Id', 'Accept-Language', 'X-Insight-Report', 'X-Lang']) {
      const v = header(first, name);
      if (v) headers.set(name, v);
    }
    headers.set('Content-Type', 'application/json');

    let items: BatchItem[];
    try {
      const res = await send({
        ...first,
        method: 'post',
        url: `${API_PREFIX}/batch`,
        baseURL: '',
        params: undefined,
        headers,
        data: JSON.stringify({ requests: unique.map((path, i) => ({ id: String(i), path })) }),
        responseType: 'text',
        signal: undefined,
        validateStatus: () => true,
      } as InternalAxiosRequestConfig);
      if (res.status === 404 || res.status === 405) batchingDisabled = true;
      if (res.status !== 200) throw new Error(`batch ${res.status}`);
      const parsed = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
      items = parsed.responses;
    } catch {
      live.forEach(sendAlone); // never lose a request to a batch failure
      return;
    }
    const byId = new Map(items.map((it) => [it.id, it]));
    const used = new Set<string>();
    live.forEach((p) => {
      const id = idOf.get(p.path) as string;
      let it = byId.get(id);
      // A shared response: every caller but the first gets its own copy (callers may change their data).
      if (it && used.has(id)) it = { ...it, body: structuredClone(it.body) };
      used.add(id);
      // UAT DEF-201: a redirect (the API's trailing-slash 307) isn't followed inside a batch — send that call alone.
      const redirect = !!it && it.status >= 300 && it.status < 400;
      if (it && !redirect) settle(p, it); else sendAlone(p);
    });
  }

  function flush() {
    timer = null;
    const all = [...groups.values()];
    groups.clear();
    for (const list of all) {
      for (let i = 0; i < list.length; i += MAX_PER_BATCH) {
        void sendBatch(list.slice(i, i + MAX_PER_BATCH));
      }
    }
  }

  return (config: InternalAxiosRequestConfig) => {
    const path = batchablePath(config);
    if (path === null) return send(config);
    return new Promise<AxiosResponse>((resolve, reject) => {
      // Requests are only batched with others for the same user/tenant (and open shared report).
      const key = `${header(config, 'Authorization')}|${header(config, 'X-Tenant-ID')}|${header(config, 'X-Insight-Report')}`;
      const list = groups.get(key) ?? [];
      list.push({ config, path, resolve, reject });
      groups.set(key, list);
      if (!timer) timer = setTimeout(flush, WINDOW_MS);
    });
  };
}
